import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select as MuiSelect,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search, RefreshCw, ArrowRight, Activity,
  CheckCircle2, AlertTriangle, Database, ShieldAlert, Clock,
  ArrowLeft, Trash2, Server, XCircle, Play, Undo2, Settings
} from 'lucide-react';

import apiManagerApi from './api';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import ConnectionsTab from './ConnectionsTab';
import MappingsTab from './MappingsTab';
import IngestOpsTab from './IngestOpsTab';
import LiveIngestPanel from './LiveIngestPanel';
import BrowserPanel from './BrowserPanel';

type WorkspaceApi = {
  api_id: string;
  api_name?: string;
  provider?: string;
  base_url?: string;
  auth_type?: string;
  status?: string;
  group_key?: string;
  group_override?: string | null;
  group_resolved?: string;
  tags?: string[];
  owner?: string | null;
  criticality?: string | null;
  activity?: {
    last_ingest_at?: string | null;
    failures?: number;
    throughput?: number;
    freshness?: string;
    next_run_at?: string | null;
  };
  endpoints?: any[];
  pipelines?: any[];
  ingest_history?: any[];
  description?: string;
};

interface Props {
  onResumeApi?: (apiId: string) => void;
  onPipelineSelect?: (pipelineId: string | null) => void;
  onOpenCreateAssistant?: () => void;
  activePipelineId: string | null;
  browserSessionId: string | null;
  browserScreenshotUrl: string | null;
  setBrowserSessionId: (id: string | null) => void;
  setBrowserScreenshotUrl: (url: string | null) => void;
}

const criticalityOptions = ['low', 'medium', 'high', 'critical'];

export default function ApiRegistryTab({
  onResumeApi,
  onPipelineSelect,
  activePipelineId,
  browserSessionId,
  browserScreenshotUrl,
  setBrowserSessionId,
  setBrowserScreenshotUrl,
}: Props) {
  const scopedAppId = useSelectedAppId();
  const [items, setItems] = useState<WorkspaceApi[]>([]);
  const [groups, setGroups] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedApiId, setSelectedApiId] = useState<string | null>(null);
  const [selected, setSelected] = useState<WorkspaceApi | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagsInput, setTagsInput] = useState('');

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const loadWorkspace = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await apiManagerApi.listWorkspaceApis({
        search: search || undefined,
        group: groupFilter === 'all' ? undefined : groupFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 300,
        app_id: scopedAppId || undefined,
      });
      const nextItems = resp.data?.apis || resp.data?.items || [];
      setItems(nextItems);
      setGroups(resp.data?.groups || {});
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load API workspace');
      setItems([]);
      setGroups({});
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (apiId: string) => {
    try {
      setDetailLoading(true);
      const resp = await apiManagerApi.getWorkspaceApiDetail(apiId, { history_limit: 25 });
      const item = resp.data?.item || null;
      setSelected(item);
      setTagsInput(Array.isArray(item?.tags) ? item.tags.join(', ') : '');
      const firstPipeline = Array.isArray(item?.pipelines) && item.pipelines[0]?.pipeline_id
        ? String(item.pipelines[0].pipeline_id)
        : null;
      onPipelineSelect?.(firstPipeline);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load API detail');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => { loadWorkspace(); }, [scopedAppId]);

  useEffect(() => {
    const t = setTimeout(() => loadWorkspace(), 250);
    return () => clearTimeout(t);
  }, [search, groupFilter, statusFilter]);

  useEffect(() => {
    if (selectedApiId) loadDetail(selectedApiId);
  }, [selectedApiId]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter(a => a.status === 'active').length;
    const pendingAuth = items.filter(a => a.status === 'pending_authentication').length;
    const errorCount = items.filter(a => a.status === 'error').length;
    const withFailures = items.filter(a => (a.activity?.failures || 0) > 0).length;
    return { total, active, pendingAuth, errorCount, withFailures };
  }, [items]);

  const groupedItems = useMemo(() => {
    return items.reduce((acc: Record<string, WorkspaceApi[]>, item) => {
      const key = String(item.group_resolved || 'general');
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [items]);

  const saveOverview = async () => {
    if (!selected?.api_id) return;
    try {
      setSaving(true);
      await Promise.all([
        apiManagerApi.updateApi(selected.api_id, {
          api_name: selected.api_name,
          provider: selected.provider,
          base_url: selected.base_url,
          description: selected.description,
        }),
        apiManagerApi.updateWorkspaceMeta(selected.api_id, {
          group_override: selected.group_override || null,
          owner: selected.owner || null,
          criticality: selected.criticality || null,
          tags: tagsInput
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
        }),
      ]);
      await loadWorkspace();
      await loadDetail(selected.api_id);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save API');
    } finally {
      setSaving(false);
    }
  };

  const runPipeline = async (pipelineId: string) => {
    try {
      await apiManagerApi.runPipeline(pipelineId);
      onPipelineSelect?.(pipelineId);
      await loadDetail(selectedApiId!);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to run pipeline');
    }
  };

  const deleteEndpoint = async (endpointId: string) => {
    if (!selected?.api_id || !endpointId) return;
    if (!window.confirm(`Delete endpoint "${endpointId}"?`)) return;
    try {
      await apiManagerApi.deleteEndpoint(selected.api_id, endpointId);
      await loadDetail(selected.api_id);
      await loadWorkspace();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete endpoint');
    }
  };

  const replayIngest = async (ingestId: string) => {
    try {
      await apiManagerApi.replayIngest(ingestId);
      await loadDetail(selectedApiId!);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to replay ingest');
    }
  };

  const deleteSelectedApi = async () => {
    if (!selected?.api_id) return;
    if (!window.confirm(`Delete API "${selected.api_name || selected.api_id}"?`)) return;
    try {
      await apiManagerApi.deleteApi(selected.api_id);
      setSelectedApiId(null);
      setSelected(null);
      onPipelineSelect?.(null);
      await loadWorkspace();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to delete API');
    }
  };

  /* ------------------------------------------------------------------ */
  /* Grid View (Agent Manager style)                                     */
  /* ------------------------------------------------------------------ */
  if (!selectedApiId) {
    return (
      <div className="flex flex-col">
        {error && (
          <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-4 flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)}><XCircle className="w-4 h-4" /></button>
          </div>
        )}

        {/* ── Quick Stats ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total APIs', value: stats.total, icon: Database, color: 'text-violet-600', bg: 'bg-violet-50' },
            { label: 'Active', value: stats.active, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Needs Auth', value: stats.pendingAuth, icon: ShieldAlert, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Failing', value: stats.withFailures, icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
            { label: 'Error', value: stats.errorCount, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
          ].map((s) => (
            <Card key={s.label} className={`${s.bg} border-0 shadow-sm`}>
              <CardContent className="p-3.5 flex items-center gap-3">
                <s.icon className={`w-7 h-7 ${s.color} opacity-80`} />
                <div>
                  <p className="text-lg font-bold text-gray-900 leading-none">{s.value}</p>
                  <p className="text-[10px] text-gray-500 font-medium mt-0.5">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ── Filters ─────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search APIs by name, provider..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white"
            />
          </div>
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="text-sm border rounded-md px-3 py-2 bg-white text-gray-700 h-10"
          >
            <option value="all">All Groups</option>
            {Object.keys(groups).sort().map((g) => (
              <option key={g} value={g}>{g} ({groups[g]})</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border rounded-md px-3 py-2 bg-white text-gray-700 h-10"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending_configuration">Pending config</option>
            <option value="pending_authentication">Needs auth</option>
            <option value="error">Error</option>
          </select>
          <Button variant="ghost" size="icon" onClick={loadWorkspace} disabled={loading} className="h-10 w-10">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <CircularProgress size={26} />
          </div>
        ) : (
          Object.keys(groupedItems).sort().map((groupKey) => {
            const groupApis = groupedItems[groupKey];
            return (
              <div key={groupKey} className="mb-8">
                {/* Category header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Server className="w-4.5 h-4.5 text-orange-600" />
                  </div>
                  <div className="flex items-center gap-3 flex-1">
                    <h2 className="text-sm font-bold uppercase tracking-wide text-orange-800">
                      {groupKey}
                    </h2>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-medium">
                      {groupApis.length} {groupApis.length === 1 ? 'api' : 'apis'}
                    </Badge>
                    <div className="flex-1 h-px bg-orange-200" />
                  </div>
                </div>

                {/* API Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {groupApis.map((api) => {
                    const statusColors: Record<string, string> = {
                      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                      pending_authentication: 'bg-amber-100 text-amber-700 border-amber-200',
                      pending_configuration: 'bg-blue-100 text-blue-700 border-blue-200',
                      error: 'bg-red-100 text-red-700 border-red-200',
                    };
                    const failures = api.activity?.failures || 0;

                    return (
                      <Card
                        key={api.api_id}
                        className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer group overflow-hidden border border-gray-200 hover:border-gray-300"
                        onClick={() => setSelectedApiId(api.api_id)}
                      >
                        <div className="h-1.5 bg-gradient-to-r from-orange-400 to-amber-400" />
                        <CardContent className="p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0 shadow-sm">
                                <Database className="w-6 h-6 text-orange-600" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-base font-bold text-gray-900 truncate leading-tight">
                                  {api.api_name || api.api_id}
                                </h3>
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {api.provider || 'Unknown provider'}
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-orange-500 transition-colors flex-shrink-0" />
                          </div>

                          <div className="flex flex-wrap items-center gap-1.5 mb-3">
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 ${statusColors[api.status || ''] || 'bg-gray-100 text-gray-600'}`}
                            >
                              {api.status || 'unknown'}
                            </Badge>
                            {failures > 0 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-50 text-red-700 border-red-200">
                                <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                                {failures} failures
                              </Badge>
                            )}
                            {api.auth_type && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                {api.auth_type}
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center justify-between text-[11px] text-gray-400 pt-2 border-t border-gray-100">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3 h-3" />
                              <span>Freshness: {api.activity?.freshness || 'unknown'}</span>
                            </div>
                            {api.activity?.throughput !== undefined && (
                              <span className="font-mono">{api.activity.throughput} events</span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Detail View                                                         */
  /* ------------------------------------------------------------------ */
  return (
    <div className="flex flex-col">
      {/* Top Header for Detail View */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => setSelectedApiId(null)}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to APIs
        </Button>
        <div className="flex items-center gap-2">
          {selected && (
            <>
              <Button variant="outline" size="sm" onClick={() => onResumeApi?.(selected.api_id)}>
                <Settings className="w-4 h-4 mr-1.5" />
                Configure
              </Button>
              <Button variant="destructive" size="sm" onClick={deleteSelectedApi}>
                <Trash2 className="w-4 h-4 mr-1.5" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <Box sx={{ display: 'grid', gridTemplateColumns: isSmallScreen ? '1fr' : '62% 38%', gap: 3 }}>
        {/* Left Side: Detail Forms */}
        <Paper variant="outlined" sx={{ p: 2, minHeight: 700 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          )}
          {!detailLoading && selected && (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ flex: 1, fontWeight: 600 }}>{selected.api_name || selected.api_id}</Typography>
                <Chip size="small" label={String(selected.status || 'unknown')} color={selected.status === 'active' ? 'success' : 'default'} />
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Live status</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <Chip label={`Freshness: ${selected.activity?.freshness || 'unknown'}`} />
                  <Chip label={`Last ingest: ${selected.activity?.last_ingest_at ? new Date(selected.activity.last_ingest_at).toLocaleString() : 'never'}`} />
                  <Chip label={`Failures: ${selected.activity?.failures || 0}`} color={(selected.activity?.failures || 0) > 0 ? 'warning' : 'default'} />
                  <Chip label={`Events: ${selected.activity?.throughput || 0}`} />
                </Stack>
              </Paper>

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={500}>Overview</Typography></AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="API Name"
                        value={selected.api_name || ''}
                        onChange={(e) => setSelected((s) => (s ? { ...s, api_name: e.target.value } : s))}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Provider"
                        value={selected.provider || ''}
                        onChange={(e) => setSelected((s) => (s ? { ...s, provider: e.target.value } : s))}
                      />
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      label="Base URL"
                      value={selected.base_url || ''}
                      onChange={(e) => setSelected((s) => (s ? { ...s, base_url: e.target.value } : s))}
                    />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Group Override"
                        placeholder={selected.group_key || 'auto'}
                        value={selected.group_override || ''}
                        onChange={(e) => setSelected((s) => (s ? { ...s, group_override: e.target.value } : s))}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Owner"
                        value={selected.owner || ''}
                        onChange={(e) => setSelected((s) => (s ? { ...s, owner: e.target.value } : s))}
                      />
                      <FormControl size="small" fullWidth>
                        <InputLabel>Criticality</InputLabel>
                        <MuiSelect
                          value={selected.criticality || ''}
                          label="Criticality"
                          onChange={(e) => setSelected((s) => (s ? { ...s, criticality: String(e.target.value) || null } : s))}
                        >
                          <MenuItem value="">None</MenuItem>
                          {criticalityOptions.map((x) => (
                            <MenuItem key={x} value={x}>{x}</MenuItem>
                          ))}
                        </MuiSelect>
                      </FormControl>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tags (comma separated)"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                    />
                    <Box>
                      <MuiButton variant="contained" onClick={saveOverview} disabled={saving} disableElevation>
                        {saving ? 'Saving...' : 'Save API setup'}
                      </MuiButton>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={500}>Endpoints</Typography></AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {(selected.endpoints || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No endpoints discovered yet.</Typography>
                    )}
                    {(selected.endpoints || []).map((ep: any) => {
                      const endpointId = String(ep.endpoint_id || ep.id || '');
                      return (
                        <Paper key={endpointId || ep.path} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {String(ep.method || 'GET').toUpperCase()} {String(ep.path || endpointId || '/')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {endpointId || 'endpoint'}
                              </Typography>
                            </Box>
                            {!!endpointId && (
                              <MuiButton size="small" color="error" variant="outlined" onClick={() => deleteEndpoint(endpointId)}>
                                Remove
                              </MuiButton>
                            )}
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={500}>Runtime pipelines</Typography></AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {(selected.pipelines || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No pipelines configured yet.</Typography>
                    )}
                    {(selected.pipelines || []).map((pipeline: any) => (
                      <Paper key={pipeline.pipeline_id || pipeline.id} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {pipeline.name || pipeline.pipeline_id || pipeline.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              status: {String(pipeline.status || 'unknown')} | schedule: {String(pipeline.schedule || 'manual')}
                            </Typography>
                          </Box>
                          <MuiButton
                            size="small"
                            variant="outlined"
                            startIcon={<Play className="w-4 h-4" />}
                            onClick={() => runPipeline(String(pipeline.pipeline_id || pipeline.id))}
                          >
                            Run now
                          </MuiButton>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={500}>Ingest history / replay</Typography></AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {(selected.ingest_history || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No ingest history yet.</Typography>
                    )}
                    {(selected.ingest_history || []).slice(0, 20).map((h: any) => (
                      <Paper key={h.ingest_id || h.id} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                              {String(h.status || 'unknown')} | {h.endpoint_id || h.pipeline_id || 'unknown source'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {h.created_at ? new Date(h.created_at).toLocaleString() : 'unknown time'}
                            </Typography>
                          </Box>
                          {!!h.ingest_id && (
                            <MuiButton
                              size="small"
                              variant="outlined"
                              startIcon={<Undo2 className="w-4 h-4" />}
                              onClick={() => replayIngest(String(h.ingest_id))}
                            >
                              Replay
                            </MuiButton>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={500}>Advanced controls (hidden by default)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Connections and credentials</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <ConnectionsTab />
                      </Paper>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Mappings</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <MappingsTab />
                      </Paper>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Ingest operations</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <IngestOpsTab />
                      </Paper>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
        </Paper>

        {/* Right Side: Live/Browser Panels */}
        <Box>
          {browserSessionId ? (
            <BrowserPanel
              sessionId={browserSessionId}
              screenshotStreamUrl={browserScreenshotUrl || undefined}
              onUserDone={() => {
                // User signaled login complete -- browser-credential-agent will continue
              }}
              onClose={() => {
                setBrowserSessionId(null);
                setBrowserScreenshotUrl(null);
              }}
            />
          ) : (
            <LiveIngestPanel
              pipelineId={activePipelineId}
              onPipelineChange={onPipelineSelect || (() => {})}
            />
          )}
        </Box>
      </Box>
    </div>
  );
}
